import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CircleUserRound, TriangleAlert, UsersRound } from 'lucide-react';

import { orgQueryOptions } from '@/features/identity';
import type { OrgRef } from '@/features/identity';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';

import type { OwnerlessBranch } from '../model/types';
import {
  useAcceptInherited,
  useAssignOwner,
  useOwnerless,
  useOwnerlessBranch,
} from '../queries/useOwnerless';
import styles from './Ownerless.module.css';

/**
 * Lista robocza „elementy bez własnego właściciela" (decyzja 106, krok K8, wariant O2).
 *
 * ⭐⭐ **Ekran otwiera się na kubełku „nikt w całym łańcuchu", choć jest mniejszy** — i to jest
 * cała jego teza. Po wsadzie na sto tysięcy elementów „dziedziczy po X" ma dziesiątki tysięcy
 * pozycji i jest stanem zwykle poprawnym: ktoś nad tym odpowiada. „Nikt" bywa listą
 * kilkudziesięciu pozycji i jest **realną dziurą w odpowiedzialności** — nie ma kogo zapytać.
 * Otwarcie na większym kubełku pokazywałoby pracę, której nie trzeba wykonywać.
 *
 * ⛔ „Przyjmuję dziedziczenie" **nie zapisuje właściciela**: dziedziczenie po `has` działa samo
 * (decyzja 46), więc nie ma czego wstawiać. Zapisuje ślad, że człowiek to obejrzał — bez niego
 * ta sama lista wraca nazajutrz w komplecie.
 */
export function OwnerlessScreen() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();

  const summary = useOwnerless(null);

  const [branch, setBranch] = useState<{ source: number | null } | null>(null);
  const [reviewed, setReviewed] = useState(false);

  if (summary.isPending) {
    return (
      <div className={styles['screen']} aria-busy="true">
        <Skeleton width="18rem" height="2rem" announce />
        <Skeleton height="6rem" />
        <Skeleton height="12rem" />
      </div>
    );
  }

  if (summary.isError) {
    return (
      <div className={styles['screen']}>
        <div className={styles['failure']} role="alert">
          <p>{describeError(summary.error)}</p>
          <Button onClick={() => void summary.refetch()}>{t('common.action.retry')}</Button>
        </div>
      </div>
    );
  }

  const data = summary.data;
  const nothingToDo = data.nobodyCount === 0 && data.inheritedCount === 0;

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t('owners.ownerless.title')}</h1>
        <p className={styles['lead']}>{t('owners.ownerless.lead')}</p>
      </header>

      {nothingToDo ? (
        <p className={styles['empty']}>{t('owners.ownerless.allCovered')}</p>
      ) : (
        <>
          <div className={styles['buckets']}>
            <Bucket
              tone="alarm"
              label={t('owners.ownerless.nobody')}
              hint={t('owners.ownerless.nobodyHint')}
              count={data.nobodyCount}
              reviewed={data.nobody.reduce((sum, item) => sum + item.reviewedCount, 0)}
            />
            <Bucket
              tone="calm"
              label={t('owners.ownerless.inherited')}
              hint={t('owners.ownerless.inheritedHint')}
              count={data.inheritedCount}
              reviewed={data.inherited.reduce((sum, item) => sum + item.reviewedCount, 0)}
            />
          </div>

          <div className={styles['columns']}>
            <nav className={styles['branches']} aria-label={t('owners.ownerless.branches')}>
              {data.nobody.map((item) => (
                <BranchButton
                  key="nobody"
                  branch={item}
                  selected={branch?.source === null && branch !== null}
                  onSelect={() => setBranch({ source: null })}
                />
              ))}

              {data.inherited.map((item) => (
                <BranchButton
                  key={item.sourceElementId ?? 'nobody'}
                  branch={item}
                  selected={branch?.source === item.sourceElementId}
                  onSelect={() => setBranch({ source: item.sourceElementId })}
                />
              ))}
            </nav>

            {branch === null ? (
              <p className={styles['pick']}>{t('owners.ownerless.pickBranch')}</p>
            ) : (
              <BranchWork
                key={String(branch.source)}
                source={branch.source}
                reviewed={reviewed}
                onReviewed={setReviewed}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Bucket({
  tone,
  label,
  hint,
  count,
  reviewed,
}: {
  tone: 'alarm' | 'calm';
  label: string;
  hint: string;
  count: number;
  reviewed: number;
}) {
  const { t, i18n } = useTranslation();
  const format = new Intl.NumberFormat(i18n.language);

  return (
    <section className={styles['bucket']} data-tone={tone}>
      <h2 className={styles['bucketLabel']}>
        {tone === 'alarm' ? <TriangleAlert size={14} aria-hidden="true" /> : null}
        {label}
      </h2>
      <p className={styles['bucketCount']}>{format.format(count)}</p>
      <p className={styles['bucketHint']}>
        {hint}
        {reviewed > 0 ? ` · ${t('owners.ownerless.reviewedCount', { count: reviewed })}` : ''}
      </p>
    </section>
  );
}

function BranchButton({
  branch,
  selected,
  onSelect,
}: {
  branch: OwnerlessBranch;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useTranslation();
  const format = new Intl.NumberFormat(i18n.language);

  const nobody = branch.sourceElementId === null;

  return (
    <button
      type="button"
      className={styles['branch']}
      aria-pressed={selected}
      data-tone={nobody ? 'alarm' : 'calm'}
      onClick={onSelect}
    >
      <span className={styles['branchName']}>
        {nobody
          ? t('owners.ownerless.nobody')
          : (branch.sourceTitle ?? t('owners.ownerless.unnamedSource'))}
      </span>
      <span className={styles['branchCount']}>{format.format(branch.count)}</span>
    </button>
  );
}

function BranchWork({
  source,
  reviewed,
  onReviewed,
}: {
  source: number | null;
  reviewed: boolean;
  onReviewed: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);

  const page = useOwnerlessBranch(source, null, reviewed, offset, true);
  const accept = useAcceptInherited(source, null, reviewed, offset);
  const assign = useAssignOwner(source, null, reviewed, offset);

  function acceptOne(elementId: number) {
    accept.mutate(
      { elementIds: [elementId] },
      { onError: (error) => show({ tone: 'danger', message: describeError(error) }) },
    );
  }

  return (
    <div className={styles['work']}>
      <div className={styles['toolbar']}>
        <fieldset className={styles['filter']}>
          <legend className={styles['srOnly']}>{t('owners.ownerless.scope')}</legend>

          <button
            type="button"
            className={styles['filterButton']}
            aria-pressed={!reviewed}
            onClick={() => {
              onReviewed(false);
              setOffset(0);
            }}
          >
            {t('owners.ownerless.toDo')}
          </button>

          <button
            type="button"
            className={styles['filterButton']}
            aria-pressed={reviewed}
            onClick={() => {
              onReviewed(true);
              setOffset(0);
            }}
          >
            {t('owners.ownerless.seen')}
          </button>
        </fieldset>

        {/* ⚠ Hurtowe przyjęcie działa na CAŁE poddrzewo, także na to, czego nie widać na
            stronie — dlatego mówi to wprost, zamiast udawać, że dotyczy widocznych wierszy. */}
        {source !== null ? (
          <Button
            variant="ghost"
            disabled={accept.isPending}
            onClick={() =>
              accept.mutate(
                { branchRootId: source },
                {
                  onSuccess: (count) =>
                    show({ tone: 'success', message: t('owners.ownerless.acceptedBulk', { count }) }),
                  onError: (error) => show({ tone: 'danger', message: describeError(error) }),
                },
              )
            }
          >
            <Check size={15} aria-hidden="true" />
            {t('owners.ownerless.acceptBranch')}
          </Button>
        ) : null}
      </div>

      {page.isPending ? (
        <div aria-busy="true" className={styles['loading']}>
          <Skeleton height="2.5rem" announce />
          <Skeleton height="2.5rem" />
          <Skeleton height="2.5rem" />
        </div>
      ) : page.isError ? (
        <div className={styles['failure']} role="alert">
          <p>{describeError(page.error)}</p>
          <Button onClick={() => void page.refetch()}>{t('common.action.retry')}</Button>
        </div>
      ) : page.data.items.length === 0 ? (
        <p className={styles['empty']}>
          {reviewed ? t('owners.ownerless.emptySeen') : t('owners.ownerless.emptyToDo')}
        </p>
      ) : (
        <>
          <ul className={styles['rows']}>
            {page.data.items.map((item) => (
              <li key={item.id} className={styles['row']}>
                <a className={styles['rowTitle']} href={`/element/${item.id}`}>
                  {item.title}
                </a>

                {item.type ? <span className={styles['rowType']}>{item.type}</span> : null}

                {item.reviewed ? (
                  <span className={styles['rowSeen']}>
                    <Check size={13} aria-hidden="true" />
                    {t('owners.ownerless.seenMark')}
                  </span>
                ) : (
                  <span />
                )}

                <div className={styles['rowActions']}>
                  <Button variant="ghost" onClick={() => setPicked(picked === item.id ? null : item.id)}>
                    {t('owners.ownerless.assign')}
                  </Button>

                  {source !== null && !item.reviewed ? (
                    <Button variant="ghost" onClick={() => acceptOne(item.id)}>
                      {t('owners.ownerless.accept')}
                    </Button>
                  ) : null}
                </div>

                {picked === item.id ? (
                  <OwnerPicker
                    busy={assign.isPending}
                    onPick={(ref) =>
                      assign.mutate(
                        { elementId: item.id, owner: { ownerKind: ref.kind, ownerId: ref.id } },
                        {
                          onSuccess: () => {
                            setPicked(null);
                            show({
                              tone: 'success',
                              message: t('owners.ownerless.assigned', { name: ref.name }),
                            });
                          },
                          onError: (error) =>
                            show({ tone: 'danger', message: describeError(error) }),
                        },
                      )
                    }
                  />
                ) : null}
              </li>
            ))}
          </ul>

          <div className={styles['pager']}>
            <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>
              {t('common.action.prev')}
            </Button>
            <span className={styles['pagerInfo']}>
              {t('owners.ownerless.range', {
                from: offset + 1,
                to: Math.min(offset + 50, page.data.total),
                total: page.data.total,
              })}
            </span>
            <Button
              variant="ghost"
              disabled={offset + 50 >= page.data.total}
              onClick={() => setOffset(offset + 50)}
            >
              {t('common.action.next')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Wskazanie właściciela.
 *
 * ⚠ Szuka po organizacji **serwerowo** — lista ludzi i zespołów w firmie na 100+ deweloperów
 * nie mieści się w pamięci ekranu, a filtrowanie lokalne kłamałoby przy pierwszym przekroczeniu
 * limitu odpowiedzi.
 */
function OwnerPicker({ busy, onPick }: { busy: boolean; onPick: (ref: OrgRef) => void }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();

  const [query, setQuery] = useState('');
  const org = useQuery(orgQueryOptions(query));

  const results = useMemo(() => org.data ?? [], [org.data]);

  return (
    <div className={styles['picker']}>
      <label className={styles['pickerLabel']}>
        {t('owners.ownerless.search')}
        <input
          className={styles['pickerInput']}
          type="search"
          value={query}
          placeholder={t('owners.ownerless.searchPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
          /*
           * Pole pojawia się WYŁĄCZNIE po kliknięciu „Przypisz właściciela", więc przeniesienie
           * ogniska spełnia intencję, a nie porywa ją.
           */
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </label>

      {org.isError ? (
        <p className={styles['pickerError']} role="alert">
          {describeError(org.error)}
        </p>
      ) : (
        <ul className={styles['pickerResults']}>
          {results.map((ref) => (
            <li key={`${ref.kind}-${ref.id}`}>
              <button
                type="button"
                className={styles['pickerOption']}
                disabled={busy}
                onClick={() => onPick(ref)}
              >
                {ref.kind === 'team' ? (
                  <UsersRound size={14} aria-hidden="true" />
                ) : (
                  <CircleUserRound size={14} aria-hidden="true" />
                )}
                {ref.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
